import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  CardMedia,
  Chip,
  IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';

const dashboardTemplates = [
  {
    id: 1,
    name: 'Executive Overview',
    description: 'High-level KPIs and company performance metrics for leadership',
    category: 'Executive',
    widgets: 8,
    starred: true,
    color: '#008080',
    previewType: 'executive',
  },
  {
    id: 2,
    name: 'Sales Performance',
    description: 'Track sales pipeline, conversion rates, and team performance',
    category: 'Sales',
    widgets: 12,
    starred: true,
    color: '#F4A261',
    previewType: 'sales',
  },
  {
    id: 3,
    name: 'Marketing Analytics',
    description: 'Campaign performance, lead generation, and ROI tracking',
    category: 'Marketing',
    widgets: 10,
    starred: false,
    color: '#003366',
    previewType: 'marketing',
  },
  {
    id: 4,
    name: 'Financial Summary',
    description: 'Revenue, expenses, cash flow, and budget vs actuals',
    category: 'Finance',
    widgets: 9,
    starred: false,
    color: '#008080',
    previewType: 'finance',
  },
  {
    id: 5,
    name: 'Customer Success',
    description: 'Customer health scores, NPS, churn risk, and support metrics',
    category: 'Customer',
    widgets: 7,
    starred: false,
    color: '#F4A261',
    previewType: 'customer',
  },
  {
    id: 6,
    name: 'Operations Monitor',
    description: 'Real-time operational metrics and process efficiency',
    category: 'Operations',
    widgets: 11,
    starred: false,
    color: '#003366',
    previewType: 'operations',
  },
];

// Dashboard preview component with mini visualizations
const DashboardPreview: React.FC<{ type: string; color: string }> = ({ type, color }) => {
  return (
    <Box
      sx={{
        height: 120,
        bgcolor: color,
        p: 1.5,
        display: 'flex',
        gap: 1,
      }}
    >
      {type === 'executive' && (
        <>
          {/* KPI cards row */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', gap: 0.5, flex: 1 }}>
              {[0, 1, 2].map((i) => (
                <Box key={i} sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.25)', borderRadius: 0.5, p: 0.5 }}>
                  <Box sx={{ width: '60%', height: 6, bgcolor: 'rgba(255,255,255,0.5)', borderRadius: 0.5, mb: 0.5 }} />
                  <Box sx={{ width: '40%', height: 10, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 0.5 }} />
                </Box>
              ))}
            </Box>
            {/* Chart area */}
            <Box sx={{ flex: 2, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 0.5, display: 'flex', alignItems: 'flex-end', p: 0.5, gap: 0.25 }}>
              {[35, 55, 45, 70, 60, 80, 65, 75].map((h, i) => (
                <Box key={i} sx={{ flex: 1, height: `${h}%`, bgcolor: 'rgba(255,255,255,0.6)', borderRadius: '2px 2px 0 0' }} />
              ))}
            </Box>
          </Box>
          {/* Side pie chart */}
          <Box sx={{ width: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.3)', border: '6px solid rgba(255,255,255,0.6)' }} />
          </Box>
        </>
      )}

      {type === 'sales' && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {/* Funnel-like visualization */}
          <Box sx={{ display: 'flex', gap: 0.5, flex: 1 }}>
            <Box sx={{ flex: 2, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 0.5, p: 0.5 }}>
              {[100, 75, 50, 30].map((w, i) => (
                <Box key={i} sx={{ width: `${w}%`, height: 12, bgcolor: `rgba(255,255,255,${0.3 + i * 0.15})`, borderRadius: 0.5, mb: 0.25, mx: 'auto' }} />
              ))}
            </Box>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              {[0, 1, 2].map((i) => (
                <Box key={i} sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.25)', borderRadius: 0.5 }} />
              ))}
            </Box>
          </Box>
          {/* Progress bars */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {[80, 60, 45].map((w, i) => (
              <Box key={i} sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 0.5, height: 20, p: 0.5 }}>
                <Box sx={{ width: `${w}%`, height: '100%', bgcolor: 'rgba(255,255,255,0.6)', borderRadius: 0.5 }} />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {type === 'marketing' && (
        <Box sx={{ flex: 1, display: 'flex', gap: 0.5 }}>
          {/* Line chart */}
          <Box sx={{ flex: 2, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 0.5, p: 0.5, position: 'relative' }}>
            <svg width="100%" height="100%" viewBox="0 0 100 60" preserveAspectRatio="none">
              <polyline fill="rgba(255,255,255,0.3)" stroke="none" points="0,60 0,45 15,40 30,35 45,25 60,30 75,20 90,15 100,10 100,60" />
              <polyline fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="2" points="0,45 15,40 30,35 45,25 60,30 75,20 90,15 100,10" />
            </svg>
          </Box>
          {/* Metrics */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {[0, 1, 2, 3].map((i) => (
              <Box key={i} sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.25)', borderRadius: 0.5, p: 0.3 }}>
                <Box sx={{ width: '50%', height: 4, bgcolor: 'rgba(255,255,255,0.5)', borderRadius: 0.5, mb: 0.25 }} />
                <Box sx={{ width: '70%', height: 6, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 0.5 }} />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {type === 'finance' && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {/* Top row with numbers */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {[0, 1, 2, 3].map((i) => (
              <Box key={i} sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.25)', borderRadius: 0.5, p: 0.5 }}>
                <Box sx={{ width: '40%', height: 4, bgcolor: 'rgba(255,255,255,0.5)', borderRadius: 0.5, mb: 0.25 }} />
                <Box sx={{ width: '60%', height: 8, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 0.5 }} />
              </Box>
            ))}
          </Box>
          {/* Stacked bar chart */}
          <Box sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 0.5, display: 'flex', alignItems: 'flex-end', p: 0.5, gap: 0.5 }}>
            {[{ a: 40, b: 30 }, { a: 50, b: 25 }, { a: 35, b: 40 }, { a: 60, b: 20 }, { a: 45, b: 35 }].map((h, i) => (
              <Box key={i} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ height: `${h.b}%`, bgcolor: 'rgba(255,255,255,0.4)', borderRadius: '2px 2px 0 0' }} />
                <Box sx={{ height: `${h.a}%`, bgcolor: 'rgba(255,255,255,0.7)' }} />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {type === 'customer' && (
        <Box sx={{ flex: 1, display: 'flex', gap: 0.5 }}>
          {/* Gauge / NPS */}
          <Box sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ width: 50, height: 25, borderRadius: '50px 50px 0 0', bgcolor: 'rgba(255,255,255,0.3)', position: 'relative', overflow: 'hidden' }}>
              <Box sx={{ position: 'absolute', bottom: 0, left: 0, width: '70%', height: '100%', bgcolor: 'rgba(255,255,255,0.6)', borderRadius: '50px 0 0 0' }} />
            </Box>
          </Box>
          {/* Health scores */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {[85, 72, 90, 65].map((w, i) => (
              <Box key={i} sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 0.5, p: 0.3, display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: w > 80 ? 'rgba(100,255,100,0.8)' : w > 60 ? 'rgba(255,200,100,0.8)' : 'rgba(255,100,100,0.8)', mr: 0.5 }} />
                <Box sx={{ flex: 1, height: 4, bgcolor: 'rgba(255,255,255,0.3)', borderRadius: 0.5 }}>
                  <Box sx={{ width: `${w}%`, height: '100%', bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 0.5 }} />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {type === 'operations' && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {/* Status indicators */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {['rgba(100,255,100,0.7)', 'rgba(100,255,100,0.7)', 'rgba(255,200,100,0.7)', 'rgba(100,255,100,0.7)'].map((c, i) => (
              <Box key={i} sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.25)', borderRadius: 0.5, p: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: c }} />
              </Box>
            ))}
          </Box>
          {/* Timeline / process flow */}
          <Box sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 0.5, display: 'flex', alignItems: 'center', p: 0.5, gap: 0.25 }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <React.Fragment key={i}>
                <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: i < 3 ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.3)' }} />
                {i < 4 && <Box sx={{ flex: 1, height: 3, bgcolor: i < 3 ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.2)' }} />}
              </React.Fragment>
            ))}
          </Box>
          {/* Metrics row */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {[0, 1, 2].map((i) => (
              <Box key={i} sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.25)', borderRadius: 0.5, height: 25 }} />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const SampleDashboards: React.FC = () => {
  return (
    <Box>
      <Typography variant="h4" sx={{ color: 'secondary.main', fontWeight: 700, mb: 1 }}>
        Dashboards
      </Typography>
      <Typography sx={{ color: 'text.secondary', mb: 3 }}>
        Pre-built dashboard templates for quick insights
      </Typography>

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h5" sx={{ color: 'secondary.main', fontWeight: 700 }}>
            {dashboardTemplates.length}
          </Typography>
          <Typography sx={{ color: 'text.secondary' }}>Templates</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h5" sx={{ color: 'secondary.main', fontWeight: 700 }}>
            {dashboardTemplates.filter(d => d.starred).length}
          </Typography>
          <Typography sx={{ color: 'text.secondary' }}>Favorites</Typography>
        </Box>
      </Box>

      {/* Dashboard Grid */}
      <Grid container spacing={3}>
        {dashboardTemplates.map((dashboard) => (
          <Grid item xs={12} sm={6} md={4} key={dashboard.id}>
            <Card
              sx={{
                border: 1,
                borderColor: 'divider',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'box-shadow 0.2s',
                '&:hover': {
                  boxShadow: 3,
                  cursor: 'pointer',
                },
              }}
            >
              {/* Preview Area */}
              <Box sx={{ position: 'relative' }}>
                <DashboardPreview type={dashboard.previewType} color={dashboard.color} />
                {/* Overlay icon */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(0,0,0,0.3)',
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    '&:hover': { opacity: 1 },
                  }}
                >
                  <VisibilityIcon sx={{ color: 'white', fontSize: 40 }} />
                </Box>
              </Box>

              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="h6" sx={{ color: 'secondary.main', fontWeight: 600 }}>
                    {dashboard.name}
                  </Typography>
                  <IconButton size="small" sx={{ mt: -0.5 }}>
                    {dashboard.starred ? (
                      <StarIcon sx={{ color: 'warning.main' }} />
                    ) : (
                      <StarBorderIcon sx={{ color: 'grey.400' }} />
                    )}
                  </IconButton>
                </Box>

                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, flexGrow: 1 }}>
                  {dashboard.description}
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Chip
                    label={dashboard.category}
                    size="small"
                    sx={{
                      bgcolor: 'grey.100',
                      color: 'text.secondary',
                      fontWeight: 500,
                    }}
                  />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {dashboard.widgets} widgets
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default SampleDashboards;
